import { useCallback } from "react";
import { Flex, Text, NativeSelect } from "@chakra-ui/react";
import { Table } from "@tanstack/react-table";
import {
    FastForwardRightIcon,
    ChevronLeftIcon,
    FastForwardLeftIcon,
    ChevronRightIcon
} from "@digital-services-gard-as/anchor-icons";
import { Button } from "@digital-services-gard-as/anchor-react";
import i18n from "../../../../../i18n";

// eslint-disable-next-line no-magic-numbers
const ROWS_PER_PAGE_OPTIONS: number[] = [10, 25, 50, 100];

const DEFAULT_ROWS_PER_PAGE_TEXT = "Show rows per page:";

interface PaginationProps<TData> {
    readonly table: Table<TData>;
    readonly rowCount: number;
    readonly id: string;
}

function Pagination<TData>({ table, rowCount: totalResults, id }: PaginationProps<TData>) {
    const { pageIndex, pageSize } = table.getState().pagination;
    const lastPage = Math.max(1, Math.ceil(totalResults / pageSize));
    const returnedResults = table.getRowModel().rows.length;
    const startValue = returnedResults ? pageSize * pageIndex + 1 : 0;
    const endValue = returnedResults + pageSize * pageIndex;

    const handlePageSizeChange = useCallback(
        (size: number) => {
            table.setPageSize(size);
            table.setPageIndex(0);
        },
        [table]
    );

    const goToFirst = useCallback(() => table.setPageIndex(0), [table]);
    const goToLast = useCallback(() => table.setPageIndex(lastPage - 1), [table, lastPage]);
    const goPrev = useCallback(() => {
        if (pageIndex > 0) {
            table.previousPage();
        }
    }, [table, pageIndex]);

    const goNext = useCallback(() => {
        if ((pageIndex + 1) * pageSize < totalResults) {
            table.nextPage();
        }
    }, [table, pageIndex, pageSize, totalResults]);

    const onPageSizeChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            handlePageSizeChange(Number(e.target.value));
        },
        [handlePageSizeChange]
    );

    const disablePrev = pageIndex === 0 || totalResults === 0;
    const disableNext = (pageIndex + 1) * pageSize >= totalResults || totalResults === 0;

    return (
        <Flex
            data-testid={`pagination-${id}`}
            alignItems="center"
            w="100%"
            position="sticky"
            bottom={0}
            zIndex={1}
            bg="anchor.white"
            p="0.5rem 1.5rem"
            gap="1rem"
            flexWrap={{ base: "wrap", lg: "nowrap" }}
            justifyContent="space-between"
        >
            <Flex alignItems="center" gap="0.5rem" color="anchor.gray.60" flexWrap={{ base: "wrap", md: "nowrap" }}>
                <Flex alignItems="center" gap="0.5rem" flexWrap="nowrap" minW={0}>
                    <Text whiteSpace="nowrap">{DEFAULT_ROWS_PER_PAGE_TEXT}</Text>
                    <NativeSelect.Root>
                        <NativeSelect.Field
                            id={`${id}-rowsPerPage`}
                            maxW="5rem"
                            value={pageSize}
                            onChange={onPageSizeChange}
                            tabIndex={0}
                            flexShrink={0}
                        >
                            {ROWS_PER_PAGE_OPTIONS.map(option => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                    </NativeSelect.Root>
                    <Text id={`${id}-results`} whiteSpace="nowrap">
                        {startValue}-{Math.min(endValue, totalResults)} of {totalResults}
                    </Text>
                </Flex>
            </Flex>
            <Flex alignItems="center" justifyContent="end" gap="1.5rem" flexDir={{ base: "column-reverse", lg: "row" }}>
                <Button
                    id={`${id}-firstPageBtn`}
                    aria-label={i18n.t("claimsInvoicesOverview:ariaLabels.firstPage")}
                    variant="tertiary"
                    size="lg"
                    onClick={goToFirst}
                    disabled={disablePrev}
                >
                    <FastForwardLeftIcon />
                </Button>
                <Button
                    id={`${id}-backBtn`}
                    aria-label={i18n.t("claimsInvoicesOverview:ariaLabels.back")}
                    variant="tertiary"
                    size="lg"
                    onClick={goPrev}
                    disabled={disablePrev}
                >
                    <ChevronLeftIcon />
                </Button>
                <Button
                    id={`${id}-nextBtn`}
                    aria-label={i18n.t("claimsInvoicesOverview:ariaLabels.next")}
                    variant="tertiary"
                    size="lg"
                    onClick={goNext}
                    disabled={disableNext}
                >
                    <ChevronRightIcon />
                </Button>
                <Button
                    id={`${id}-lastPageBtn`}
                    aria-label={i18n.t("claimsInvoicesOverview:ariaLabels.lastPage")}
                    variant="tertiary"
                    size="lg"
                    onClick={goToLast}
                    disabled={disableNext}
                >
                    <FastForwardRightIcon />
                </Button>
            </Flex>
        </Flex>
    );
}

export default Pagination;
